<template>
  <div>
    <!--
      Bootstrap interop stubs.
      These invisible b-modals intercept BootstrapVue calls ($bvModal.show / $root.$emit)
      and redirect to the corresponding V2Modals.
    -->
    <b-modal
      id="clear-retailer-cart"
      hide-header
      hide-footer
      hide-header-close
      no-enforce-focus
      @show="interceptClearRetailerCart"
    />
    <b-modal
      id="import_cart_modal"
      hide-header
      hide-footer
      hide-header-close
      no-enforce-focus
      @show="interceptImportCart"
    />
    <b-modal
      v-if="importCartModalId !== 'import_cart_modal'"
      :id="importCartModalId"
      hide-header
      hide-footer
      hide-header-close
      no-enforce-focus
      @show="interceptImportCart"
    />

    <!-- Clear Cart V2Modal -->
    <v2-modal
      :show="showClearCart"
      title="Clear my cart"
      size="sm"
      @close="showClearCart = false"
    >
      <p style="margin-bottom: 0.5rem">
        The cart will be permanently cleared.
      </p>
      <p>Are you sure you want to proceed?</p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showClearCart = false"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="destructive"
          @click="onConfirmClearCart"
        >
          Yes, clear it
        </v2-button>
      </template>
    </v2-modal>

    <!-- Import Cart V2Modal -->
    <v2-modal
      :show="showImportCart"
      title="Upload Cart"
      size="md"
      @close="closeImportModal"
    >
      <!-- File drop zone -->
      <div
        v-if="!cartUploadFile"
        class="v2-cart-modals__upload-area"
        @click="$refs.fileInput.click()"
      >
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <polyline points="16 16 12 12 8 16" />
          <line
            x1="12"
            y1="12"
            x2="12"
            y2="21"
          />
          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
        </svg>
        <h4>
          <b>Drag & Drop</b>
        </h4>
        <p>or click to select files from your system</p>
      </div>

      <!-- Selected file -->
      <div
        v-if="cartUploadFile"
        class="v2-cart-modals__file-selected"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--v2-success)"
          stroke-width="2"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line
            x1="16"
            y1="13"
            x2="8"
            y2="13"
          />
          <line
            x1="16"
            y1="17"
            x2="8"
            y2="17"
          />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        <div class="v2-cart-modals__file-info">
          <div class="name">
            {{ cartUploadFile.name }}
          </div>
          <div class="size">
            {{ (cartUploadFile.size / 1024).toFixed(1) }} KB
          </div>
        </div>
        <button
          class="v2-cart-modals__file-remove"
          @click="handleCartUploadFileRemove"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button>
      </div>

      <!-- Hidden file input -->
      <input
        ref="fileInput"
        type="file"
        accept=".xlsx"
        style="display: none"
        @change="onFileChange"
      >

      <template #footer>
        <v2-button
          variant="outline"
          @click="closeImportModal"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="!cartUploadFile || isUploading"
          :loading="isUploading"
          @click="handleImportCart"
        >
          {{ isUploading ? 'Uploading...' : 'Upload' }}
        </v2-button>
      </template>
    </v2-modal>

    <!-- Cart Conflict V2Modal -->
    <v2-modal
      :show="showCartConflict"
      title="Add to Cart"
      size="sm"
      @close="showCartConflict = false"
    >
      <p>{{ cartUploadConflictMessage }}</p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showCartConflict = false"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          @click="onConfirmCartConflict"
        >
          Yes, I understand
        </v2-button>
      </template>
    </v2-modal>

    <!-- Clear Retailer Cart V2Modal -->
    <v2-modal
      :show="showClearRetailerCart"
      title="Delete retailer cart"
      size="sm"
      @close="showClearRetailerCart = false"
    >
      <p style="margin-bottom: 0.5rem">
        Retailer has some items in their cart.
      </p>
      <p>Are you sure you want to proceed to delete the retailer's cart?</p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showClearRetailerCart = false"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="destructive"
          @click="onClickDeleteRetailerCart"
        >
          Yes, delete it
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import { BModal } from 'bootstrap-vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'
import analytics from '@/@core/utils/analytics'
import constants, { FILE_FORMATS } from '@/constants'
import { formatObject, parseErrorObject } from '@/@core/utils/utils'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { FETCH_CART_COUNT, IMPORT_CART } from '@/store/modules/shop.module'
import { apiToastError, apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'
import NotificationMixin from '@/views/mixins/NotificationMixin.vue'

const { FILE_TYPES } = c

export default {
  name: 'V2CartModals',
  components: {
    BModal,
    V2Modal,
    V2Button,
  },
  mixins: [NotificationMixin, UserRoleMixin],
  props: {
    isPopup: {
      type: Boolean,
      default: false,
    },
    retailerId: {
      type: String,
      default: '',
    },
    clearCart: {
      type: Function,
      default: () => {},
    },
    onCartImportSuccess: {
      type: Function,
      default: () => {},
    },
    placeOrder: {
      type: Function,
      default: () => {},
    },
    cartProducts: {
      type: Object,
      default: () => {},
    },
    onClickExportCart: {
      type: Function,
      default: () => {},
    },
    importCartModalId: {
      type: String,
      default: 'import_cart_modal',
    },
  },
  emits: [
    'on-close-import-cart-modal',
    'on-import-cart-file-select',
    'on-import-cart-file-remove',
    'on-import-cart-confirm',
    'on-import-cart-failed',
  ],
  data() {
    return {
      showClearCart: false,
      showImportCart: false,
      showCartConflict: false,
      showClearRetailerCart: false,
      isUploading: false,
      cartUploadFile: null,
      cartUploadConflictMessage: '',
      importCart: false,
    }
  },
  methods: {
    // ─── Public API ──────────────────────────────────────────────────────
    show(id) {
      if (id === 'clear-cart') this.showClearCart = true
      else if (id === this.importCartModalId || id === 'import_cart_modal') this.showImportCart = true
      else if (id === 'modal-upload-cart-conflict') this.showCartConflict = true
      else if (id === 'clear-retailer-cart') this.showClearRetailerCart = true
    },
    hide(id) {
      if (id === 'clear-cart') this.showClearCart = false
      else if (id === this.importCartModalId || id === 'import_cart_modal') this.showImportCart = false
      else if (id === 'modal-upload-cart-conflict') this.showCartConflict = false
      else if (id === 'clear-retailer-cart') this.showClearRetailerCart = false
    },

    // ─── Bootstrap interop ───────────────────────────────────────────────
    interceptClearRetailerCart(bvEvent) {
      // BootstrapVue fires @show with a BvModalEvent before animation.
      // preventDefault() cancels the Bootstrap modal from ever appearing,
      // then we show the V2Modal instead.
      if (bvEvent && typeof bvEvent.preventDefault === 'function') {
        bvEvent.preventDefault()
      }
      this.showClearRetailerCart = true
    },
    interceptImportCart(bvEvent) {
      if (bvEvent && typeof bvEvent.preventDefault === 'function') {
        bvEvent.preventDefault()
      }
      this.showImportCart = true
    },

    // ─── Clear cart ──────────────────────────────────────────────────────
    async onConfirmClearCart() {
      this.showClearCart = false
      await this.clearCart()
    },

    // ─── Import cart ─────────────────────────────────────────────────────
    onFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.cartUploadFile = file
        this.$emit('on-import-cart-file-select', file.name)
      }
      // Reset input so the same file can be re-selected
      e.target.value = ''
    },
    handleCartUploadFileRemove() {
      this.$emit('on-import-cart-file-remove', this.cartUploadFile?.name)
      this.cartUploadFile = null
    },
    handleImportCart() {
      this.$emit('on-import-cart-confirm', this.cartUploadFile?.name)
      this.syncCartProducts()
    },
    closeImportModal() {
      this.showImportCart = false
      this.cartUploadFile = null
      this.$emit('on-close-import-cart-modal')
    },
    async handleImportCartSuccess(res) {
      this.closeImportModal()
      apiToastSuccess(res.data.message)
      this.onCartImportSuccess()
      this.isUploading = false
      this.cartUploadFile = null
      if (!this.isPopup) await this.$store.dispatch(FETCH_CART_COUNT)
      if (this.isRetailer) {
        analytics.track(
          constants.TRACKS.ACTIONS.RETAILER_UPLOADS_CART,
          formatObject({ fileFormat: FILE_FORMATS.XLSX, status: 'Success' }),
        )
      }
    },
    handleImportCartError(err) {
      this.$emit('on-import-cart-failed', this.cartUploadFile?.name, err)
      this.isUploading = false
      const hasOtherErrors = this.checkForFailedCarts(err) || this.checkForCartConflicts(err) || this.checkForRetailerCart(err)
      if (hasOtherErrors) return
      if (this.isRetailer) {
        analytics.track(
          constants.TRACKS.ACTIONS.RETAILER_UPLOADS_CART,
          formatObject({ fileFormat: FILE_FORMATS.XLSX, status: 'Error', Error: parseErrorObject(err) }),
        )
      }
    },
    checkForFailedCarts(err) {
      const failedCarts = err?.response?.data?.data?.failedCarts ?? []
      if (failedCarts.length) {
        failedCarts.forEach(errObj => {
          apiToastError(`${errObj?.storeName} - ${errObj?.error}`)
        })
        return true
      }
      return false
    },
    checkForCartConflicts(err) {
      if (err?.response?.status === 425) {
        this.cartUploadConflictMessage = err.response.data.message || err.message
        this.showImportCart = false
        this.showCartConflict = true
        return true
      }
      return false
    },
    checkForRetailerCart(err) {
      if (err?.response?.data?.data?.hasRetailerCart) {
        this.importCart = true
        this.showClearRetailerCart = true
        return true
      }
      return false
    },
    async syncCartProducts(event, shouldProceed = false) {
      if (this.cartUploadFile) {
        this.isUploading = true
        const cartUploadFileLink = await this.$store.dispatch(UPLOAD_DOCUMENTS, {
          fileType: FILE_TYPES.CART_UPLOAD,
          file: this.cartUploadFile,
        }).catch(err => {
          this.handleImportCartError(err)
        })
        if (!cartUploadFileLink) return
        const payload = { cartProductsFile: cartUploadFileLink }
        const params = {}
        if (shouldProceed) params.shouldProceed = true
        if (this.isPopup) payload.retailerId = this.retailerId
        this.$store
          .dispatch(IMPORT_CART, { payload, params })
          .then(async res => {
            await this.handleImportCartSuccess(res)
          })
          .catch(err => {
            this.handleImportCartError(err)
          })
      } else {
        apiToastWarning('Please Select a File')
      }
    },

    // ─── Cart conflict ───────────────────────────────────────────────────
    onConfirmCartConflict() {
      this.showCartConflict = false
      this.syncCartProducts(false, true)
    },

    // ─── Clear retailer cart ─────────────────────────────────────────────
    async onClickDeleteRetailerCart() {
      this.showClearRetailerCart = false
      if (this.importCart) {
        this.importCart = false
        await this.clearCart(this.retailerId)
        this.syncCartProducts()
        return
      }
      if (this.cartProducts?.isCartExport) {
        await this.clearCart(this.retailerId)
        this.onClickExportCart()
      } else {
        this.placeOrder(true)
      }
    },
  },
}
</script>
